/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { AdcModel, BackupModel, DashboardDataModel, ScheduleModel } from '../../../shared/models';
import { AdcsApiService, ScheduleApiService } from '../../../shared/http';
import { AdcsUtilsService, NamespaceService, PortalRbacService } from '../../../shared/services/utils';
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { DashboardService } from '../../../shared/services/component';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LanguageService, LogService, ModalService, RbacService, UtilsService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { VendorEnum } from '../../../shared/enums';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-scheduler',
    templateUrl: './scheduler.component.html',
    styleUrls: ['./scheduler.component.scss']
})

export class SchedulerComponent extends BaseComponent {
    /**
     * Flag on loading state.
     */
    public isLoading: boolean = true;

    /**
     * Form group name.
     */
    public schedulerForm: FormGroup;

    /**
     * Step state.
     */
    public step: number = 1;

    /**
     * Dropdown content.
     */
    public dropdown = {
        product: [
            <DropdownModel>{ key: 'adc', value: 'ADC' }
        ],
        schedule: [
            <DropdownModel>{ key: 'backup', value: 'Backup' },
            <DropdownModel>{ key: 'update', value: 'Update' }
        ],
        occurrence: [
            <DropdownModel>{ key: 0, value: 'Once' },
            <DropdownModel>{ key: 1, value: 'Daily' },
            <DropdownModel>{ key: 2, value: 'Weekly' },
            <DropdownModel>{ key: 3, value: 'Monthly' },
            <DropdownModel>{ key: 4, value: 'Yearly' },
        ]
    };

    /**
     * Namespace selected key.
     */
    public selectedNamespaceKey: number;

    /**
     * Table row data for step 2.
     */
    public tableRows: any[] = [];

    /**
     * Store selected row object for table state checks.
     */
    public selectedRowObject: any = [];

    /**
     * Store edit ID as a reference.
     */
    public storedId: string = undefined;

    /**
     * Store product type as a reference.
     */
    public storedProductType: string = undefined;

    /**
     * Store the schedule type as a reference.
     */
    public storedScheduleType: string = null;

    /**
     * Display users local time.
     */
    public localTime: string;

    /**
     * Store hidden dropdown content keys.
     */
    public dropdownHiddenContentKeys: any[];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store if tier is free.
     */
    public isFreeTier: boolean = true;

    /**
     * Store the products provided by the API.
     */
    private products: any[] = [];

    /**
     * Store the dashboard Data
     * @private
     */
    private dashboardData: DashboardDataModel;

    constructor(
        private adcUtilsService: AdcsUtilsService,
        private adcsApiService: AdcsApiService,
        private dashboardService: DashboardService,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private logService: LogService,
        private modalService: ModalService,
        private namespaceService: NamespaceService,
        private portalRbacService: PortalRbacService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private scheduleApiService: ScheduleApiService,
        private utilsService: UtilsService
    ) {
        super();

        const params = this.utilsService.getObjectValue(this.route, 'snapshot.queryParamMap.params');
        if (params?.id) {
            this.storedId = params.id;
            this.storedProductType = params.type;
        }

        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;

        this.setupSubscribers();
        this.setUpForm();
        this.setUpFormSubscriber();
        this.patchFormData();
        this.registerButtons();

        this.isFreeTier = this.portalRbacService.selectedTier?.tier === 'free';
    }

    /**
     * Form function triggered when a table row is selected.
     * @param event
     */
    public onSelect(event: any): void {
        const ids = event.map((item: { id: any; }) => item.id);
        this.schedulerForm.get('step2.productIds').setValue(ids);
    }

    /**
     * Get from group.
     * @param formGroupName
     */
    public getFormGroup(formGroupName: any): FormGroup {
        return this.schedulerForm.get(formGroupName) as FormGroup;
    }

    /**
     * Next step.
     */
    public nextStep(): void {
        this.step++;
    }

    /**
     * Next step.
     */
    public backStep(): void {
        this.step--;
    }

    /**
     * Get the backup details for the table.
     * @param adcId
     */
    public getBackupDetails(adcId: number): string {
        const selectedTier = this.portalRbacService.selectedTier;
        const adcBackups = this.dashboardData.backups.filter((item: BackupModel) => item.adcID === adcId);
        const tierMax = selectedTier.backups;
        if (selectedTier.tier === 'tailored') {
            return `${adcBackups.length}`;
        } else {
            return `${adcBackups.length} ${this.languageService.translate('of')} ${tierMax}`;
        }
    }

    /**
     * Navigate to new route.
     * @param url
     */
    public navigate(url: string): void {
        this.router.navigate([url]).then(() => {
        });
    }

    /**
     * Submit the schedule.
     */
    public submitSchedule(id?: string): void {
        const formValues = this.schedulerForm.getRawValue();
        const submitValues = { ...formValues.step1, ...formValues.step2 };

        // Add the 'seconds' back on the end of the time string.
        submitValues.time = submitValues.time + ':00';

        // Remove null from object before submitting.
        const cleanSubmitValues = this.utilsService.cleanObject(submitValues);

        const apiCall = id
            ? this.scheduleApiService.updateSchedule(this.selectedNamespaceKey, id, cleanSubmitValues)
            : this.scheduleApiService.createSchedule(this.selectedNamespaceKey, cleanSubmitValues);

        apiCall.pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    const navigationExtras: NavigationExtras = {
                        state: {
                            productType: submitValues.productType,
                            scheduleType: submitValues.scheduleType,
                            scheduleIds: data['ids'],
                        }
                    };
                    this.router.navigate(['/cm/scheduler/schedules'], navigationExtras).then(() => {
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ScheduleListComponent:submitSchedule', error.message);
                }
            });
    }

    /**
     * Confirm delete schedule.
     * @param id
     */
    public confirmDeleteSchedule(id: string): void {
        const confirmationObject = {
            title: 'schedule_delete',
            message: 'schedule_delete_message',
            callBack: this.deleteSchedule.bind(this, id),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete schedule.
     * @param id
     */
    private deleteSchedule(id: string): Observable<any> {
        return new Observable((observer) => {
            const namespace = this.namespaceService.selectedNamespace;
            this.scheduleApiService.deleteSchedule(namespace.key, id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: () => {
                        this.router.navigate(['/cm/scheduler/schedules']).then(() => {
                        });
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error('ScheduleListComponent:deleteSchedule', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'scheduler-save': [
                { path: '/api/v1/:namespace/scheduler/', methods: ['POST'] },
                { path: '/api/v1/:namespace/scheduler/', methods: ['GET'] },
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] }
            ],
            'scheduler-update': [
                { path: '/api/v1/:namespace/scheduler/:id', methods: ['PATCH'] },
                { path: '/api/v1/:namespace/scheduler/', methods: ['GET'] },
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] }
            ],
            'scheduler-delete': [
                { path: '/api/v1/:namespace/scheduler/:id', methods: ['DELETE'] },
                { path: '/api/v1/:namespace/scheduler/', methods: ['GET'] },
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] }
            ],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Setup page subscribers.
     * @private
     */
    private setupSubscribers(): void {
        this.namespaceService.selectedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespace: any): void => {
                    this.selectedNamespaceKey = namespace.key;
                    const productType: string = this.schedulerForm.get('step1.productType').value;
                    this.getApiCallForProductByType(productType)
                        .pipe(takeUntil(this.destroyed$))
                        .subscribe(products => {
                            this.products = products;
                            this.populateTableRows(products);
                            this.updateSchedulesDropdown(products);
                        });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ScheduleListComponent:setupSubscribers', 'Unable to get selected namespace', error);
                }
            });
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setUpForm(): void {
        const now = new Date();
        this.localTime = this.utilsService.formatTimeForHtmlInput(now);

        const utcTime = new Date(
            Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate(),
                now.getUTCHours(),
                now.getUTCMinutes(),
                now.getUTCSeconds()
            )
        );

        const { date, time } = this.utilsService.getDateTimeObjectFromIsoDateTime(utcTime.toISOString());

        this.schedulerForm = this.formBuilder.group({
            step1: this.formBuilder.group({
                id: [null], // <-- used in edit mode
                productName: [null], // <-- used in edit mode
                productType: [null],
                scheduleType: [null, Validators.required],
                date: [date, Validators.required],
                time: [time, Validators.required],
                occurrence: [null],
            }),
            step2: this.formBuilder.group({
                productIds: [null, Validators.required]
            })
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscriber(): void {
        if (!this.storedId) {
            this.schedulerForm.get('step1.productType').valueChanges
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (productType: string): void => {
                        this.getApiCallForProductByType(productType)
                            .pipe(takeUntil(this.destroyed$))
                            .subscribe(products => {
                                this.products = products;
                                this.populateTableRows(products);
                                this.filterTableRowsByType();
                                this.updateSchedulesDropdown(products);
                            });
                    }
                });
        }

        this.schedulerForm.get('step1.scheduleType').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (scheduleType: string): void => {
                    this.storedScheduleType = scheduleType;
                    const occurrenceInput = this.schedulerForm.get('step1.occurrence');
                    if (scheduleType === 'backup') {
                        this.dashboardData = this.dashboardService.allData;
                        occurrenceInput.setValidators(Validators.required);
                    } else {
                        this.dashboardData = null;
                        occurrenceInput.clearValidators();
                    }

                    this.filterTableRowsByType();

                    occurrenceInput.updateValueAndValidity();
                }
            });

        // Show user local time.
        this.schedulerForm.get('step1.time').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (time: any): void => {
                    const date = this.schedulerForm.get('step1.date').value;
                    const dateParts = date.split('-');
                    const newTime = new Date(`${dateParts[1]}/${dateParts[2]}/${dateParts[0]} ${time} UTC`);
                    this.localTime = this.utilsService.formatTimeForHtmlInput(newTime);
                }
            });
    }

    /**
     * Filter table rows by schedule type.
     * @private
     */
    private filterTableRowsByType(): void {
        let filteredRows: any[];
        if (this.storedScheduleType === 'update') {
            filteredRows = this.tableRows.filter(item => item.updateStatus === 'available' && this.adcUtilsService.versionCanUpdate(item));
        } else {
            filteredRows = this.products;
        }

        this.tableRows = [...filteredRows];
    }

    /**
     * Get the correct API call for the product type being processed.
     * @param productType
     * @private
     */
    private getApiCallForProductByType(productType: string): Observable<any> {
        if (productType === 'adc') {
            return this.getAdcList();
        } else if (productType === 'waf') {
            // products = this.getWafList();
        }
    }

    /**
     * Patch incoming data to form.
     * @private
     */
    private patchFormData(): void {
        if (this.storedId) {
            this.getPatchData()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: ({ schedule, products }) => {
                        const product = products.find(
                            (item: AdcModel) => item.id === parseInt(this.storedId, 10)
                        );
                        this.updateSchedulesDropdown([product]);
                        const formattedData = this.formatResponseForPatch(schedule, products);
                        this.schedulerForm.patchValue(formattedData);
                        const step1 = this.schedulerForm.get('step1');
                        step1.get('productName').disable();
                        if (schedule.scheduleType) {
                            step1.get('scheduleType').disable();
                        }
                        this.isLoading = false;
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error('ScheduleListComponent:patchFormData', 'Unable to get selected namespace', error);
                    }
                });
        } else {
            this.schedulerForm.get('step1.productType').setValue('adc');
            this.isLoading = false;
        }
    }

    /**
     * Get data to patch to the form.
     * @private
     */
    private getPatchData(): Observable<any> {
        return new Observable((observer) => {
            if (this.storedId && !this.storedProductType) {
                this.scheduleApiService.getAllSchedules(this.selectedNamespaceKey)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (schedules: any): void => {
                            const schedule: ScheduleModel = schedules.find(
                                (item: any) => item.id === parseInt(this.storedId, 10)
                            );
                            this.getApiCallForProductByType(schedule.product)
                                .pipe(takeUntil(this.destroyed$))
                                .subscribe({
                                    next: (products: any): void => {
                                        observer.next({ schedule, products });
                                        observer.complete();
                                    },
                                    error: (error: ErrorResponseModel): void => {
                                        this.logService.error(
                                            'ScheduleListComponent:getPatchData',
                                            'Unable to get product by type',
                                            error
                                        );
                                    }
                                });
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error(
                                'ScheduleListComponent:getPatchData',
                                'Unable to get all schedules',
                                error
                            );
                        }
                    });
            } else if (this.storedProductType) {
                this.getApiCallForProductByType(this.storedProductType)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (products: any) => {
                            const schedule = {
                                productType: this.storedProductType,
                                applianceIDs: [this.storedId]
                            };

                            observer.next({ schedule, products });
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel) => {
                            this.logService.error(
                                'ScheduleListComponent:getPatchData',
                                'Unable to get product by type',
                                error
                            );
                        }
                    });
            }
        });
    }

    /**
     * Format patch data
     * @param schedule
     * @param products
     * @private
     */
    private formatResponseForPatch(schedule: any, products: any[]): any {
        const product = this.storedId ? products.find(item => item.id.toString() === this.storedId.toString()) : null;
        let date: any;
        let time: any;

        if (schedule.scheduleTime) {
            ({ date, time } = this.utilsService.getDateTimeObjectFromIsoDateTime(schedule.scheduleTime.toISOString()));
        } else {
            date = this.schedulerForm.get('step1.date').value;
            time = this.schedulerForm.get('step1.time').value;
        }

        const productIds = schedule.applianceIDs.map((item: any) => parseInt(item, 10));

        return {
            step1: {
                id: this.storedId,
                productName: product?.label,
                productType: schedule.productType,
                scheduleType: schedule.scheduleType,
                date,
                time,
                occurrence: schedule.occurrence,
            },
            step2: {
                productIds: productIds
            }
        };
    }

    /**
     * Get the most upto date list of ADCs.
     * @private
     */
    private getAdcList(): Observable<any[]> {
        return new Observable((observer) => {
            this.adcsApiService.getAll(this.selectedNamespaceKey)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (adcs: any) => {
                        observer.next(this.filterAdcs(adcs));
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Filter adcs that are adopted.
     * @param adcs
     * @private
     */
    private filterAdcs(adcs: AdcModel[]): any[] {
        const isIncludedVendor: VendorEnum[] = [
            VendorEnum.LBENTERPRISE,
            VendorEnum.LBENDURANCE,
            VendorEnum.KEMP,
            VendorEnum.F5,
            VendorEnum.CITRIX
        ];

        return adcs.filter((item: AdcModel): string => {
            if (isIncludedVendor.includes(item.vendor)) {
                return item.label;
            }
        });
    }

    /**
     * Populate table rows.
     * @param products
     * @private
     */
    private populateTableRows(products: any[]): void {
        // Format the version.
        this.tableRows = products.map(item => {
            return { ...item, version: this.adcUtilsService.formatVersionNumber(item.version) };
        });
    }

    /**
     * Update schedules dropdown.
     * @private
     */
    private updateSchedulesDropdown(products: any[]): void {
        if (!products.some(item => item.updateStatus === 'available' && this.adcUtilsService.versionCanUpdate(item))) {
            this.dropdown.schedule = [<DropdownModel>{ key: 'backup', value: 'Backup' }];
        }
    }
}
